import {
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { FclLocationSurcharge } from "../../location/location-surcharge/location-surcharge-fcl.model";
import { LocationSurchargeRepository } from "../../location/location-surcharge/location-surcharge.repository";
import { LocationSurcharge } from "../../location/location-surcharge/location-surcharge.type";
import { Location } from "../../location/location.model";
import { eventType } from "../../notify/event.const";
import { NotifyService } from "../../notify/notify.service";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { noop } from "../../shared/noop.function";
import { FclQuote } from "../models/quote-fcl.model";
import { LclQuote } from "../models/quote-lcl.model";
import { surchargeAssignment } from "./assignment.const";
import { QuoteLocationSurchargeRepository } from "./quote-location-surcharge.repository";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { FclTariff } from "../models/quote-fcl-tariff.model";
import { ContainerName } from "../../shared/container/container-name.type.const";
import { SortEquipmentPipe } from "../../shared/container/sort-equipment.pipe";
import { AirQuote } from "../models/quote-air.model";
import { EventService } from "../../shared/event/event.service";
import { FormatContainerLabelPipe } from "../../shared/container/format-container-label.pipe";
import { LocationSurchargeMapper } from "../../location/location-surcharge/location-surcharge.mapper";
import { BaseStandardQuote } from "../models/quote-base-standard.model";
import { quoteDirection } from "../quote-form/quote-direction.const";
import { locationSurchargeType } from "../../location/location-surcharge/location-surcharge-type.const";
import { QuoteLocationSurcharge } from "./quote-location-surcharge.type";
import { containerNames } from "../../shared/container/container.const";
import { LclLocationSurcharge } from "../../location/location-surcharge/location-surcharge-lcl.model";
import { AirLocationSurcharge } from "../../location/location-surcharge/location-surcharge-air.model";
import { tableColumn } from "../../ui/table/table-column.const";
import { ITableColumn } from "../../ui/table/table-column.interface";
import { LocalCostLabel } from "../models/local-cost-label.model";
import { AirTariffQuote } from "../models/quote-airtariff.model";
import { quoteType } from "../quote-form/quote-type.const";
import { Rate } from "app/rate/rate-type";
import { SelectItem } from "primeng/api";
import { BrokerageQuote } from "../models/quote-brokerage.model";
import { LocationIconComponent } from "../../ui/location-icon/location-icon.component";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { InputText } from "primeng/inputtext";
import { TableComponent } from "../../ui/table/table.component";
import { AgentRequestRepository } from "../agent-request.repository";
import {
  AgentRequestCounts,
  agentRequestGroup,
  AgentRequestTarget,
  agentRequestType,
} from "../models/agent-request.model";
import { agentRequestListParams } from "../agent-request-list/agent-request-list-params.const";
import { AgentRequestLinksComponent } from "../agent-request-links/agent-request-links.component";
import { AgentRequestDialogComponent } from "../agent-request-dialog/agent-request-dialog.component";
import { QuoteLocation } from "../models/quote-location.model";

// Quote types whose Charges tab locations can be sent to an agent for charges.
const agentRequestQuoteTypes: readonly string[] = [
  quoteType.fcl,
  quoteType.lcl,
  quoteType.air,
];

@Component({
  selector: "app-quote-surcharge",
  templateUrl: "./quote-surcharge.component.html",
  styleUrls: ["./quote-surcharge.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LocationIconComponent,
    Bind,
    ButtonDirective,
    InputText,
    TableComponent,
    AgentRequestLinksComponent,
    AgentRequestDialogComponent,
  ],
})
export class QuoteSurchargeComponent implements OnInit, OnDestroy, OnChanges {
  private quoteLocationSurchargeRepository = inject(
    QuoteLocationSurchargeRepository,
  );
  private locationSurchargeRepository = inject(LocationSurchargeRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);
  private agentRequestRepository = inject(AgentRequestRepository);

  public readonly quote = input<FclQuote | LclQuote | FclTariff>(undefined);
  public readonly canChangeLabel = input<boolean>(true);
  public readonly rates = input<Rate[]>(undefined);
  // Off for a multi quote's children, which are plain FCL/LCL/AIR quotes themselves.
  public readonly agentRequestsVisible = input(true);
  readonly localCostLabelsChange = output<LocalCostLabel[]>();
  public loading = false;
  public locationList: Location[] = [];
  public subscription = new Subscription();

  public columns: ITableColumn[] = [];
  public selection = [];
  public newSurcharge: LocationSurcharge[] = [];

  public readonly showsAgentRequests = computed(
    () =>
      this.agentRequestsVisible() &&
      agentRequestQuoteTypes.includes(this.quote()?.getQuoteType()),
  );
  public agentRequestCounts = new AgentRequestCounts();
  private agentRequestCountSubscription: Subscription;
  // The location the request popup is open for; null while it is closed.
  public agentRequestTarget: AgentRequestTarget | null = null;

  public readonly editingLocationId = signal<number | null>(null);
  private readonly labelInput =
    viewChild<ElementRef<HTMLInputElement>>("labelInput");
  private skipCommit = false;

  constructor() {
    effect(() => {
      if (this.editingLocationId() === null) {
        return;
      }
      const input = this.labelInput();
      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  ngOnInit() {
    this.fetchLocationCharges();
    this.fetchAgentRequestCounts();
    this.updateColumns();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.quote && !changes.quote.isFirstChange()) {
      this.updateColumns();
    }

    if (changes.quote) {
      if (
        changes.quote.previousValue &&
        changes.quote.previousValue.getId() !==
          changes.quote.currentValue.getId()
      ) {
        this.fetchLocationCharges();
        this.fetchAgentRequestCounts();
        this.updateColumns();
      }
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.agentRequestCountSubscription?.unsubscribe();
  }

  public locationAgentRequestParams(
    location: Location,
  ): Record<string, string | number> {
    return {
      [agentRequestListParams.quote]: this.quote().getId(),
      [agentRequestListParams.type]: agentRequestGroup.charges,
      [agentRequestListParams.location]: location.getId(),
      [agentRequestListParams.locationName]: location.getName(),
    };
  }

  // A location listed on the tab is an origin or a destination leg of the quote; origin wins when it is both.
  public requestChargesFromAgent(location: Location): void {
    const quote = this.quote();
    const isLeg = (leg: QuoteLocation) =>
      leg.getLocation()?.getId() === location.getId();
    const originLeg =
      quote instanceof BaseStandardQuote
        ? quote.getOriginLocations().find(isLeg)
        : undefined;
    const destinationLeg =
      quote instanceof BaseStandardQuote && !originLeg
        ? quote.getDestinationLocations().find(isLeg)
        : undefined;

    this.agentRequestTarget = {
      type: originLeg
        ? agentRequestType.originCharges
        : agentRequestType.destinationCharges,
      originLegId: originLeg?.getId() ?? null,
      destinationLegId: destinationLeg?.getId() ?? null,
      originName: originLeg ? location.getName() : null,
      destinationName: originLeg ? null : location.getName(),
      inlandHaulage: (originLeg ?? destinationLeg)?.getInlandHaulage() || null,
    };
  }

  public onAgentRequestsSent(): void {
    this.agentRequestTarget = null;
    this.fetchAgentRequestCounts();
  }

  private fetchAgentRequestCounts(): void {
    this.agentRequestCountSubscription?.unsubscribe();
    this.agentRequestCounts = new AgentRequestCounts();

    const quote = this.quote();
    if (!this.showsAgentRequests() || !quote?.getId()) {
      return;
    }

    this.agentRequestCountSubscription = this.agentRequestRepository
      .getQuoteAgentRequestCounts(quote.getId())
      .subscribe({
        next: (counts) => (this.agentRequestCounts = counts),
        error: noop,
      });
  }

  public onRowSelect(location: Location, locationSurcharge: LocationSurcharge) {
    this.createQuoteLocationSurcharge(location, locationSurcharge);
  }

  public onRowUnselect(locationSurcharge: LocationSurcharge) {
    this.removeQuoteLocationSurcharge(locationSurcharge);
  }

  public onSaveSurcharge(
    location: Location,
    locationSurcharge: LocationSurcharge,
  ) {
    if (!locationSurcharge.getId()) {
      this.createLocationSurcharge(location, locationSurcharge);
    } else {
      if (locationSurcharge.getAssignment() !== surchargeAssignment.global) {
        this.saveLocationSurcharge(location, locationSurcharge);
      }
      this.saveQuoteLocationSurcharge(location, locationSurcharge);
    }
  }

  public onReorder(
    location: Location,
    event: { dragIndex: number; dropIndex: number },
  ): void {
    // PrimeNG has already moved the row inside the array backing the table, so
    // dropIndex is its final index over every surcharge, selected or not.
    const index = event.dropIndex;
    // prettier-ignore
    const quoteLocationSurcharge = location
      .getSurcharges()[index]
      .getFirstQuoteLocationSurcharge();
    const newPosition = this.calculateQuoteLocationSurchargePosition(
      location,
      quoteLocationSurcharge,
    );
    this.reorderQuoteLocationSurcharge(quoteLocationSurcharge, newPosition);
  }

  public isRowDisabled(
    columnName: string,
    locationSurcharge: LocationSurcharge,
  ): boolean {
    const canEdit =
      locationSurcharge.getAssignment() !== surchargeAssignment.global;
    let disabledForEveryone = ["total"];
    const disabledForSales = [
      ...containerNames,
      "carrier",
      "currency",
      "minPrice",
      "maxPrice",
      "chargeableWeightPrice",
      "shipmentPrice",
      "weightPrice",
      "measurementPrice",
      "documentPrice",
      "piecePrice",
    ];

    if (locationSurcharge.chargedFor == "transport") {
      if (
        locationSurcharge instanceof LclLocationSurcharge &&
        locationSurcharge.total
      ) {
        disabledForEveryone = [
          ...disabledForEveryone,
          ...containerNames,
          "currency",
        ];
      }
      if (locationSurcharge instanceof FclLocationSurcharge) {
        const data = locationSurcharge.getContainers().getPrices();
        const isCurrencyDisabled = Object.keys(data).some(
          (containerName) => !!data[containerName].value,
        );
        if (isCurrencyDisabled) {
          disabledForEveryone = [...disabledForEveryone, "currency"];
        }
      }
    }

    return (
      disabledForEveryone.indexOf(columnName) >= 0 ||
      (!canEdit && disabledForSales.indexOf(columnName) >= 0)
    );
  }

  public fetchLocationCharges() {
    this.loading = true;
    this.locationSurchargeRepository
      .listForQuote(this.quote().getId())
      .subscribe((locationList) => {
        this.loading = false;
        this.locationList = locationList;
        this.reloadSelection();
        this.initQuote();
        this.initNewSurcharge();
      });
  }

  public supportsLabel(): boolean {
    const quote = this.quote();
    return (
      quote instanceof FclQuote ||
      quote instanceof LclQuote ||
      quote instanceof AirQuote ||
      quote instanceof AirTariffQuote
    );
  }

  public getLocalCostLabel(location: Location): string {
    const quote = this.quote();
    return this.supportsLabel()
      ? (quote as BaseStandardQuote).getLocalCostLabel(location)
      : null;
  }

  public getLocalCostLabelText(location: Location): string {
    return (
      this.getLocalCostLabel(location) || `Local Costs ${location.getName()}`
    );
  }

  public startEdit(location: Location): void {
    this.skipCommit = false;
    this.editingLocationId.set(location.getId());
  }

  public commitLabel(location: Location, value: string): void {
    if (this.skipCommit) {
      this.skipCommit = false;
      return;
    }
    this.editingLocationId.set(null);
    const label = value.trim();
    if (label === (this.getLocalCostLabel(location) || "")) {
      return;
    }
    this.changeLocalCostLabel(location, label);
  }

  public cancelEdit(): void {
    this.skipCommit = true;
    this.editingLocationId.set(null);
  }

  public changeLocalCostLabel(location: Location, value: string) {
    const quote = this.quote();
    if (
      quote instanceof FclQuote ||
      quote instanceof LclQuote ||
      quote instanceof AirQuote ||
      quote instanceof AirTariffQuote
    ) {
      quote.setLocalCostLabel(location, value);
      this.localCostLabelsChange.emit(quote.getLocalCostLabels());
    }
  }

  private emitUpdateStart(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdating,
      data: this.quote(),
    });
  }

  private emitUpdateEnd(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdated,
      data: this.quote(),
    });
  }

  private createLocationSurcharge(
    location: Location,
    locationSurcharge: LocationSurcharge,
  ): void {
    this.emitUpdateStart();
    if (locationSurcharge.haveQuoteLocationSurcharges()) {
      locationSurcharge.setName(
        locationSurcharge.getFirstQuoteLocationSurcharge().getName(),
      );
    }
    this.locationSurchargeRepository
      .add(location, locationSurcharge, this.quote().getId())
      .subscribe({
        next: (newLocationSurcharge) => {
          locationSurcharge.setId(newLocationSurcharge.getId());
          this.notifyService.toast(
            TOAST.SUCCESS,
            "Location Surcharge has been created",
          );
          this.createQuoteLocationSurcharge(location, locationSurcharge);
        },
        error: (err) =>
          this.notifyService.toast(
            TOAST.ERROR,
            "Location Surcharge has not been created",
          ),
      });
  }

  private createQuoteLocationSurcharge(
    location: Location,
    locationSurcharge: LocationSurcharge,
  ): void {
    locationSurcharge.isBusy = true;
    this.quoteLocationSurchargeRepository
      .add(
        this.quote().getId(),
        this.quote().getQuoteType(),
        locationSurcharge.getId(),
        locationSurcharge.getQuoteLocationSurcharges()[0],
      )
      .subscribe({
        next: (newQuoteLocationSurcharge) => {
          locationSurcharge.isBusy = false;
          newQuoteLocationSurcharge.setName(locationSurcharge.getName());
          locationSurcharge.setQuoteLocationSurcharges([
            newQuoteLocationSurcharge,
          ]);
          this.reloadSelection();
          // By the default new location is added at the end, we have to calculate it's new position
          const newPosition = this.calculateQuoteLocationSurchargePosition(
            location,
            newQuoteLocationSurcharge,
          );
          this.reorderQuoteLocationSurcharge(
            newQuoteLocationSurcharge,
            newPosition,
          );
          this.emitUpdateEnd();
          this.eventService.eventBus.emit({
            event: eventType.quoteAverageMarginUpdated,
          });
        },
        error: noop,
      });
  }

  private saveLocationSurcharge(
    location: Location,
    locationSurcharge: LocationSurcharge,
  ) {
    this.locationSurchargeRepository.save(locationSurcharge).subscribe({
      next: (newLocationSurcharge) => {
        this.notifyService.toast(
          TOAST.SUCCESS,
          "Location Surcharge has been saved",
        );
      },
      error: (err) => {
        this.notifyService.toast(
          TOAST.ERROR,
          "Location Surcharge has not been saved",
        );
      },
    });
  }

  private saveQuoteLocationSurcharge(
    location: Location,
    locationSurcharge: LocationSurcharge,
  ) {
    this.quoteLocationSurchargeRepository
      .save(locationSurcharge.getQuoteLocationSurcharges()[0])
      .subscribe({
        next: (newQuoteLocationSurcharge) => {
          this.notifyService.toast(
            TOAST.SUCCESS,
            "Quote Location Surcharge has been saved",
          );
          this.eventService.eventBus.emit({
            event: eventType.quoteAverageMarginUpdated,
          });
        },
        error: (err) => {
          this.notifyService.toast(
            TOAST.ERROR,
            "Quote Location Surcharge has not been saved",
          );
        },
      });
  }

  private removeQuoteLocationSurcharge(
    locationSurcharge: LocationSurcharge,
  ): void {
    this.quoteLocationSurchargeRepository.delete(locationSurcharge).subscribe({
      next: (quoteLocationSurcharge) => {
        locationSurcharge.setQuoteLocationSurcharges([]);
        this.notifyService.toast(
          TOAST.SUCCESS,
          "Quote Location Surcharge has been deleted",
        );
        this.eventService.eventBus.emit({
          event: eventType.quoteLocationSurchargeDeleted,
        });
        this.eventService.eventBus.emit({
          event: eventType.quoteAverageMarginUpdated,
        });
      },
      error: (err) => {
        this.notifyService.toast(
          TOAST.ERROR,
          "Quote Location Surcharge has not been deleted",
        );
      },
    });
  }

  private calculateQuoteLocationSurchargePosition(
    location: Location,
    quoteLocationSurcharge: QuoteLocationSurcharge,
  ): number {
    let position = 0;
    const surcharges = location.getSurcharges();
    for (let i = 0; i < surcharges.length; i++) {
      if (!surcharges[i].haveQuoteLocationSurcharges()) {
        continue;
      }
      if (
        surcharges[i].getFirstQuoteLocationSurcharge().getId() ===
        quoteLocationSurcharge.getId()
      ) {
        break;
      }

      // Counts all quote location surcharges before encountering quoteLocationSurcharge
      position++;
    }

    return position;
  }

  private reorderQuoteLocationSurcharge(
    quoteLocationSurcharge: QuoteLocationSurcharge,
    position,
  ): void {
    this.subscription.add(
      this.quoteLocationSurchargeRepository
        .reorder(quoteLocationSurcharge.getId(), position)
        .subscribe({
          next: (success) => {
            this.eventService.eventBus.emit({
              event: eventType.quoteLocationSurchargeCreatedAndReordered,
            });
            this.notifyService.toast(TOAST.SUCCESS, "Charges Reordered");
          },
          error: (err) => {
            this.eventService.eventBus.emit({
              event: eventType.quoteLocationSurchargeError,
            });
            this.notifyService.toast(
              TOAST.ERROR,
              "Error",
              new BackendErrorPipe().transform(err),
            );
          },
        }),
    );
  }

  private initNewSurcharge() {
    this.newSurcharge = [];
    let discriminator = this.quote().getQuoteType();

    const quote = this.quote();
    if (quote.getQuoteType() === quoteType.airtariff) {
      discriminator = quoteType.air;
    } else if (quote.getQuoteType() === quoteType.brokerage) {
      discriminator = quoteType.fcl;
    }

    this.locationList.forEach((location) => {
      const quoteValue = this.quote();
      const newCharge = LocationSurchargeMapper.map({
        discriminator: discriminator,
        type:
          quoteValue instanceof BaseStandardQuote &&
          (quoteValue.getDirection() === quoteDirection.IMP ||
            // LTL/FTL/PTL have no direction and keep the destination default they had as imports.
            !quoteValue.getDirection())
            ? locationSurchargeType.destination
            : locationSurchargeType.origin,
        assignment: surchargeAssignment.quote,
        quoteLocationSurcharges: [
          {
            discriminator: discriminator,
            name: "",
          },
        ],
        containers: [],
        currency: "USD",
      });

      if (newCharge instanceof FclLocationSurcharge) {
        newCharge.getContainers().setAllPrices(0);
        newCharge
          .getFirstQuoteLocationSurcharge()
          .getContainers()
          .setAllPrices(0);
      }
      newCharge.getFirstQuoteLocationSurcharge().setQuote(this.quote());
      this.newSurcharge[location.getId()] = newCharge;
    });
  }

  private initQuote() {
    this.locationList.forEach((location) => {
      location.getSurcharges().forEach((surcharge) => {
        if (surcharge.haveQuoteLocationSurcharges()) {
          surcharge.getFirstQuoteLocationSurcharge().setQuote(this.quote());
        }
      });
    });
  }

  private reloadSelection() {
    this.selection = [];
    this.locationList.forEach((location) => {
      location.getSurcharges().forEach((surcharge) => {
        if (surcharge.haveQuoteLocationSurcharges()) {
          if (this.selection[location.getId()]) {
            this.selection[location.getId()].push(surcharge);
          } else {
            this.selection[location.getId()] = [surcharge];
          }
        }
      });
    });
  }

  /************ FCL **************/
  private getFclNetPrice(
    locationSurcharge: FclLocationSurcharge,
    containerName: ContainerName,
  ): number {
    return locationSurcharge.getContainers().getPrice(containerName);
  }

  private getFclMarginPrice(
    locationSurcharge: FclLocationSurcharge,
    containerName: ContainerName,
  ) {
    return locationSurcharge.haveQuoteLocationSurcharges()
      ? locationSurcharge
          .getFirstQuoteLocationSurcharge()
          .getContainers()
          .getPrice(containerName)
      : undefined;
  }

  private setFclNetPrice(
    locationSurcharge: FclLocationSurcharge,
    containerName: ContainerName,
    value: number,
  ) {
    locationSurcharge.getContainers().setPrice(containerName, value);
  }

  private setFclMarginPrice(
    locationSurcharge: FclLocationSurcharge,
    containerName: ContainerName,
    value: number,
  ) {
    locationSurcharge
      .getFirstQuoteLocationSurcharge()
      .getContainers()
      .setPrice(containerName, value);
  }

  private getFclColumns(): ITableColumn[] {
    const self = this;
    const columns = [];
    const quote = this.quote();
    if (quote instanceof FclQuote) {
      columns.push({
        header: "Shipment",
        type: tableColumn.priceBox,
        field: "shipmentPrice",
        getNet: self.getLclNetPrice,
        setNet: self.setLclNetPrice,
        getMargin: self.getLclMarginPrice,
        setMargin: self.setLclMarginPrice,
      });

      const fclColumns = new SortEquipmentPipe().transform(
        quote.getEquipment(),
      );
      fclColumns.map((containerName) => {
        columns.push({
          header: new FormatContainerLabelPipe().transform(containerName),
          type: tableColumn.priceBox,
          field: containerName,
          getNet: this.getFclNetPrice,
          setNet: this.setFclNetPrice,
          getMargin: this.getFclMarginPrice,
          setMargin: this.setFclMarginPrice,
        });
      });
    }

    return columns;
  }

  /************ LCL **************/
  private getLclNetPrice(
    locationSurcharge: LclLocationSurcharge,
    field: string,
  ): number {
    return locationSurcharge[field];
  }

  private setLclNetPrice(
    locationSurcharge: LclLocationSurcharge,
    field: string,
    value: number,
  ): void {
    locationSurcharge[field] = value;
  }

  private getLclMarginPrice(
    locationSurcharge: LclLocationSurcharge,
    field: string,
  ): number {
    return locationSurcharge.haveQuoteLocationSurcharges()
      ? locationSurcharge.getFirstQuoteLocationSurcharge()[field]
      : undefined;
  }

  private setLclMarginPrice(
    locationSurcharge: LclLocationSurcharge,
    field: string,
    value: number,
  ): void {
    locationSurcharge.getFirstQuoteLocationSurcharge()[field] = value;
  }

  private getLclMultiplier(
    locationSurcharge: LocationSurcharge,
    field: string,
  ): number {
    const quote = this.quote();
    if (quote instanceof LclQuote) {
      switch (field) {
        case "weightPrice":
          return quote.getWeightInTon();
        case "measurementPrice":
          return quote.getMeasurement();
        case "piecePrice":
          return quote.getPieces() || 1;
      }
    }
  }

  private getAirMultiplier(
    locationSurcharge: LocationSurcharge,
    field: string,
  ): number {
    const quote = this.quote();
    if (
      quote instanceof AirQuote &&
      locationSurcharge instanceof AirLocationSurcharge
    ) {
      switch (field) {
        case "weightPrice":
          return quote.getWeight();
        case "chargeableWeightPrice":
          return quote.getChargeableWeight();
        case "measurementPrice":
          return quote.getMeasurement();
        case "piecePrice":
          return quote.getPieces() || 1;
      }
    }
  }

  private getLclColumns(cols): ITableColumn[] {
    const self = this;
    const columns: ITableColumn[] = [];
    Object.keys(cols).forEach(function (key) {
      columns.push({
        header: cols[key],
        type: tableColumn.priceBox,
        field: key,
        displayKgAsTon:
          key === "weightPrice" && self.quote() instanceof LclQuote,
        getNet: self.getLclNetPrice,
        setNet: self.setLclNetPrice,
        getMargin: self.getLclMarginPrice,
        setMargin: self.setLclMarginPrice,
        getMultiplier: function (
          locationSurcharge: LocationSurcharge,
          field: string,
        ): number {
          const quote = self.quote();
          if (quote instanceof LclQuote) {
            return self.getLclMultiplier(locationSurcharge, field);
          } else if (quote instanceof AirQuote) {
            return self.getAirMultiplier(locationSurcharge, field);
          }
        },
      });
    });

    return [
      ...columns,
      { header: "Min Price", field: "minPrice", type: tableColumn.number },
      { header: "Max Price", field: "maxPrice", type: tableColumn.number },
      { header: "Total", field: "total", type: tableColumn.number },
    ];
  }

  private updateColumns() {
    const self = this;
    this.columns = [
      {
        header: "Local charge",
        field: "editedName",
        type: tableColumn.surchargeTypeAutocomplete,
      },
      {
        header: "Service",
        field: "carrier",
        type: tableColumn.service,
        getOptions: function getOptions(
          rowData: LocationSurcharge,
        ): SelectItem[] | null {
          const quote = self.quote();
          const rates = self.rates();
          if (
            rowData.getAssignment() === surchargeAssignment.global ||
            rowData.chargedFor === "transport" ||
            (quote instanceof BaseStandardQuote &&
              (!quote.getHasOrigin() || !quote.getHasDestination())) ||
            !rates
          ) {
            return null;
          } else {
            const options = [];
            for (let i = 0; i < rates.length; i++) {
              options.push({
                label: rates[i].getCarrier().getName(),
                value: rates[i].getCarrier(),
              });
            }
            return options;
          }
        },
      },
    ];

    const quote = this.quote();
    if (quote instanceof FclQuote && !(quote instanceof BrokerageQuote)) {
      this.columns = [...this.columns, ...this.getFclColumns()];
    }

    if (quote instanceof BrokerageQuote) {
      this.columns = [
        ...this.columns,
        {
          header: "Unit Display Name",
          field: "shipmentDisplayUnitName",
          type: tableColumn.default,
        },
        {
          header: "Shipment",
          type: tableColumn.priceBox,
          field: "shipmentPrice",
          getNet: self.getLclNetPrice,
          setNet: self.setLclNetPrice,
          getMargin: self.getLclMarginPrice,
          setMargin: self.setLclMarginPrice,
        },
      ];
    }

    if (quote instanceof LclQuote) {
      this.columns = [
        ...this.columns,
        ...this.getLclColumns({
          weightPrice: "Weight",
          measurementPrice: "CBM",
          shipmentPrice: "Shipment",
          piecePrice: "Piece",
          documentPrice: "Document",
        }),
      ];
    }
    if (quote instanceof AirQuote || quote instanceof AirTariffQuote) {
      this.columns = [
        ...this.columns,
        ...this.getLclColumns({
          weightPrice: "Gross Weight",
          chargeableWeightPrice: "Chargeable Weight",
          shipmentPrice: "Shipment",
          piecePrice: "Piece",
          documentPrice: "Document",
        }),
      ];
    }
    this.columns.push({
      header: "Currency",
      field: "currency",
      type: tableColumn.currency,
    });
    if (!(quote instanceof BrokerageQuote)) {
      this.columns.push({
        header: "",
        field: "editableInternalNote",
        type: tableColumn.comment,
      });
      this.columns.push({
        header: "",
        field: "info",
        type: tableColumn.info,
      });
      this.columns.push({
        header: "",
        field: "restrictedNote",
        type: tableColumn.warning,
      });
      this.columns.push({
        header: "",
        field: "debugInfoParsed",
        type: tableColumn.debug,
      });
    }
  }
}
